import type { ReactNode } from 'react';
import { type FC } from 'react';

import ErrorIcon from '@mui/icons-material/Error';
import { Checkbox, IconButton } from '@mui/material';

import styled from 'styled-components';
import { useTheme } from '@mui/material/styles';
import type { CheckboxRenderProps, WithMixed, WithUndefined } from '../../viewOption/types.ts';

const switchState = (oldState: WithMixed<WithUndefined<boolean>>): boolean => oldState !== true;

export const CheckboxRender: FC<
  CheckboxRenderProps & {
    mixedComponent: ReactNode;
    checkedComponent: ReactNode;
    uncheckedComponent: ReactNode;
  }
> = ({
  isChecked: isVisible,
  isEmptyBox,
  isDisabled,
  onChange,
  mixedComponent,
  checkedComponent,
  uncheckedComponent
}) => {
  const theme = useTheme();
  const iconColor = isDisabled ? theme.disabledFont : theme.palette.primary.light;

  if (isEmptyBox) {
    return null;
  }

  if (isVisible === undefined) {
    return <ErrorIcon />;
  }
  const handleChange = (): void => onChange(switchState(isVisible));

  if (isVisible === 'mixed') {
    return (
      <MixedButton onClick={handleChange} $iconColor={iconColor} disabled={isDisabled}>
        {mixedComponent}
      </MixedButton>
    );
  }

  return (
    <Checkbox
      checked={isVisible}
      size={'medium'}
      onChange={handleChange}
      icon={uncheckedComponent}
      checkedIcon={checkedComponent}
      inputProps={{ 'aria-label': 'controlled' }}
      style={{ color: iconColor }}
      disabled={isDisabled}
    />
  );
};

const MixedButton = styled(IconButton)<{
  $iconColor: string;
  disabled: boolean | undefined;
}>`
  margin-right: 0.1rem;
  margin-bottom: 0.1rem;

  & > svg {
    ${({ $iconColor, disabled }): string | false => !!disabled && `color: ${$iconColor};`}
  }
`;
